import dayjs from 'dayjs';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { PageLayout } from '@hero/components/layouts';
import { ProductSelectionSection } from '@hero/components/organisms';
import { useValidationErrorsStore } from '@hero/components/providers/ValidationErrorsProvider';
import { FLOW_CODES, SEND_GIFT_FLOW_STEPS } from '@hero/constants';
import { useQueries } from '@hero/hooks';
import { ROUTE_PATHS } from '@hero/routes';
import { useCreateRecognition } from '@hero/services';
import { useGetBranding } from '@hero/services/organization-service/hooks';
import { cleanObjectEmptyFields } from '@hero/utils';

import WidgetFooter from './WidgetFooter';

export function SendGiftCreateCollectionTemplate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { queries } = useQueries();
  const { data: brandingData } = useGetBranding();
  const { addError } = useValidationErrorsStore();
  const { mutateAsync: createRecognition, isLoading } = useCreateRecognition();

  const [selectedProducts, setSelectedProducts] = useState([]);

  const handleConfirm = async () => {
    if (!selectedProducts.length) {
      await addError(
        <Trans
          key="noProductAtList"
          i18nKey="errors.noSelectedProduct"
          components={{
            b: <b />
          }}
        />
      );
      return;
    }

    const response = await createRecognition({
      name: `${queries.templateTitle} (${dayjs().format('MM.DD.YY')})`,
      settings: {
        expiresAt: dayjs()
          .add(2, 'week')
          .set('hour', 23)
          .set('minute', 59)
          .set('second', 0)
          .set('millisecond', 0)
          .toISOString()
      },
      products: selectedProducts,
      experienceSettings: cleanObjectEmptyFields({
        branding: {
          giftWrapperUrl: brandingData?.branding?.giftWrappers?.default,
          logoUrl: brandingData?.branding?.logoUrl
        }
      }),
      meta: {
        creationStep: SEND_GIFT_FLOW_STEPS.review,
        occasionId: queries.templateUid
      },
      flowCode: FLOW_CODES.simplified
    });

    navigate(ROUTE_PATHS.SEND_GIFT_REVIEW(response.code));
  };

  return (
    <PageLayout
      header={{
        title: t('addItemsYourGiftCollection'),
        stepNumber: 1
      }}
      navigation={{
        rightButton: {
          children: t('continueWithSelectedItems'),
          onClick: handleConfirm,
          loading: isLoading
        }
      }}
    >
      <ProductSelectionSection
        localShoppingCartEnabled
        showSaveAsCollectionButton={false}
        selectedProducts={selectedProducts}
        onChangeSelectedProductList={selectedProducts => {
          setSelectedProducts(selectedProducts);
        }}
        onConfirmCart={handleConfirm}
        components={{
          WidgetFooter
        }}
        isDraft
      />
    </PageLayout>
  );
}
